import { EXIT_DATE_ALIAS } from 'common/constants/allocations';
import { largeCurrencyFormat } from 'common/formats/formats';
import CompanyList from 'pages/Waterfall/components/CompanyList/CompanyList';
import CompanyListValueViewer from 'pages/Waterfall/components/CompanyList/CompanyListValueViewer';
import MeasurementDateList from 'pages/Waterfall/components/MeasurementDateList/MeasurementDateList';
import MeasurementDateListValueViewer from 'pages/Waterfall/components/MeasurementDateList/MeasurementDateValueViewer';
import {
  CAP_TABLE_ALIAS,
  COMPANY_ALIAS,
  EXIT_ENTERPRISE_VALUE_ALIAS,
  EXIT_EQUITY_ALIAS,
  FIRM_TOTAL_EXIT_PROCEEDS_ALIAS,
  LESS_DEBT_ALIAS,
  MEASUREMENT_DATE_ALIAS,
  PLUS_CASH_ALIAS,
} from 'pages/Waterfall/SpreadsheetConfig/constants';
import {
  StandardExchangeRateRowLabel
} from 'pages/Waterfall/SpreadsheetConfig/FundExchangeRate/FundExchangeRateRowLabel/FundExchangeRateRowLabel';
import { InputSheetRowConfigProps } from './inputSheetRowConfig.props';
import { SelectValueViewer } from '../../../../../components';
import { GridDateEditor, GridDateValueViewer } from '../../../../../components/FeaturedSpreadsheet/components';
import CaptableList from '../../../components/CaptableList/CaptableList';

// Keep in sync with InputSheetParser
const FUND_CURRENCY_SELECTOR_ALIAS = 'fund_currency_selector';

const inputSheetRowConfig = ({
  exitDate,
  capTableId,
  equityValue,
  enterpriseValue,
  plusCash,
  lessDebt,
  firmOptions,
  childrenCapTables,
  parentCapTables,
  exitProceedsExpression,
  plusCashIsFromFinancials,
  lessDebtIsFromFinancials
}: InputSheetRowConfigProps) => {
  const allCaptables = [...(parentCapTables ?? []), ...childrenCapTables];
  // Build rows for the Input sheet. We remove dedicated fund rows and instead
  // render a fund currency column (Column C) with a top selector row when needed.
  const currencyRow = {
    headerValue: 'Currency',
    alias: FUND_CURRENCY_SELECTOR_ALIAS,
    headerAlias: 'fund_currency_header',
    value: undefined,
    tooltipMessages: [],
    valueViewer: StandardExchangeRateRowLabel,
  }
  const rows = [
    {
      headerValue: 'Cap Table',
      alias: CAP_TABLE_ALIAS,
      headerAlias: 'cap_table_header',
      tooltipMessages: [
        'Default is the primary cap table. Select a pro forma or alternate version if available.',
      ],
      value: capTableId || undefined,
      dataEditor: (props: any) => <CaptableList {...props} childrenCapTables={childrenCapTables} />,
      valueViewer: (props: any) => <SelectValueViewer {...props} useAliasList={false} options={allCaptables} />,
    },
    {
      headerValue: 'Exit Date',
      alias: EXIT_DATE_ALIAS,
      headerAlias: 'exit_date_header',
      value: exitDate || '',
      tooltipMessages: [
        'The date through which dividends and any accrued return are calculated.',
      ],
      valueViewer: (props: any) => <GridDateValueViewer {...props} />,
      dataEditor: (props: any) => <GridDateEditor {...props} />,
    },
    {
      headerValue: 'Exit Enterprise Value',
      alias: EXIT_ENTERPRISE_VALUE_ALIAS,
      headerAlias: `${EXIT_ENTERPRISE_VALUE_ALIAS}_header`,
      value: enterpriseValue ?? 0,
      tooltipMessages: [
        'Enter either Enterprise Value or Equity Value; the other is calculated using cash and debt.',
      ],
      format: largeCurrencyFormat,
      customKey: EXIT_ENTERPRISE_VALUE_ALIAS
    },
    {
      headerValue: 'Plus Cash',
      alias: PLUS_CASH_ALIAS,
      headerAlias: `${PLUS_CASH_ALIAS}_header`,
      value: plusCash,
      tooltipMessages: [
        'Pulled from the Balance Sheet by default (displayed green). Override with custom inputs if desired (displayed blue).',
      ],
      format: largeCurrencyFormat,
      readOnly: false,
      className: 'align-right',
      style: plusCashIsFromFinancials ? { color: 'green' } : {},
    },
    {
      headerValue: 'Less Debt',
      alias: LESS_DEBT_ALIAS,
      headerAlias: `${LESS_DEBT_ALIAS}_header`,
      value: lessDebt,
      tooltipMessages: [
        'Pulled from the Balance Sheet by default (displayed green). Override with custom inputs if desired (displayed blue).',
      ],
      format: largeCurrencyFormat,
      readOnly: false,
      className: 'align-right',
      style: lessDebtIsFromFinancials ? { color: 'green' } : {},
    },
    {
      headerValue: 'Exit Equity Value',
      alias: EXIT_EQUITY_ALIAS,
      headerAlias: 'exit_equity_value_header',
      value: equityValue ?? 0,
      tooltipMessages: [
        'Enter either Enterprise Value or Equity Value; the other is calculated using cash and debt.',
      ],
      format: largeCurrencyFormat,
      customKey: EXIT_EQUITY_ALIAS,
    },
    {
      headerValue: 'Firm Total Exit Proceeds',
      alias: FIRM_TOTAL_EXIT_PROCEEDS_ALIAS,
      value: undefined,
      headerAlias: `${FIRM_TOTAL_EXIT_PROCEEDS_ALIAS}_header`,
      format: largeCurrencyFormat,
      expr: exitProceedsExpression,
    },
  ];
  if (firmOptions) {
    const firmOptionRows = [
      {
        headerValue: 'Company Name',
        alias: COMPANY_ALIAS,
        headerAlias: 'company_header',
        value: firmOptions.companyId,
        valueViewer: (props: any) => <CompanyListValueViewer {...props} />,
        dataEditor: (props: any) => <CompanyList {...props} />,
      },
      {
        headerValue: 'Measurement Date',
        alias: MEASUREMENT_DATE_ALIAS,
        tooltipMessages: [
          'The date of the analysis, reflecting the company’s information as of that time.',
        ],
        headerAlias: 'measurement_date_header',
        value: firmOptions.measurementDateId,
        valueViewer: (props: any) => <MeasurementDateListValueViewer {...props} />,
        dataEditor: (props: any) => <MeasurementDateList {...props} />,
      },
    ];
    return [currencyRow, ...firmOptionRows, ...rows];
  }
  return rows;
};

export default inputSheetRowConfig;
